import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import _ from "lodash";

import {
	Typography,
	Box,
	Divider,
	ListItem,
	List,
	ListItemIcon,
	ListItemText,
	IconButton,
	TextField,
	Menu,
	MenuItem,
	Button,
	Skeleton,
	Tooltip,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SettingsIcon from "@mui/icons-material/Settings";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SaveIcon from "@mui/icons-material/Save";

import { useAppSelector, useAppDispatch } from "../../store/hooks.ts";
import {
	selectLoadingDetail,
	selectPatientCache,
	selectSelectedItem,
	setSelectedItem,
	updateCache,
} from "../../store/slices/searchSlice.ts";
import { calculateEndTime } from "../calendar/helper/eventRendering.tsx";
import { setSelectedSession } from "../../store/slices/sessionSlice.ts";
import { setSelectedEvent } from "../../store/slices/eventSlice.ts";
import {
	setCalendarDate,
	setCalendarView,
	setSelectedEntityType,
} from "../../store/slices/calendarSlice.ts";
import globalAxios from "../../globalAxios.js";
import { SERVER_URL } from "../../config.js";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { playSuccessSave } from "../../components/sounds/index.ts";

const PatientView = () => {
	const dispatch = useAppDispatch();

	const navigate = useNavigate();

	const selectedItem = useAppSelector(selectSelectedItem);
	const patientCache = useAppSelector(selectPatientCache);
	const loadingDetail = useAppSelector(selectLoadingDetail);

	const cachedPatient = patientCache[selectedItem.id];
	const enrichedPatient =
		cachedPatient &&
		(cachedPatient.events?.length ?? 0) > (selectedItem.events?.length ?? 0)
			? cachedPatient
			: selectedItem;

	const [isEditing, setIsEditing] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);

	// Local editable state
	const [localPatient, setLocalPatient] = useState({ ...enrichedPatient });
	const [emailError, setEmailError] = useState(false);

	const [savingPatient, setSavingPatient] = useState(false);
	const [saveSuccess, setSaveSuccess] = useState(false);

	const [originalPatient, setOriginalPatient] = useState(enrichedPatient);

	const changesMade = useMemo(() => {
		return !_.isEqual(localPatient, originalPatient);
	}, [localPatient, originalPatient]);

	useEffect(() => {
		setLocalPatient({ ...enrichedPatient });
		setOriginalPatient(enrichedPatient);
	}, [enrichedPatient]);

	if (!selectedItem || selectedItem.type !== "patient") return null;

	const handleChange = (field) => (e) => {
		setLocalPatient({ ...localPatient, [field]: e.target.value });
	};

	const handleMenuClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleToggleEdit = () => {
		setIsEditing(true);
		handleMenuClose();
	};

	const handleCancel = () => {
		setIsEditing(false);
		setLocalPatient({ ...enrichedPatient }); // Reset edits
		handleMenuClose();
	};

	const handleSave = async () => {
		if (!localPatient) return;

		setSavingPatient(true);

		try {
			const res = await globalAxios.put(
				`${SERVER_URL}/patients/${localPatient.id}`,
				localPatient
			);

			if (res.status === 200 || res.status === 204) {
				playSuccessSave();

				setSaveSuccess(true);
				setIsEditing(false);
				dispatch(updateCache({ ...localPatient, type: "patient" }));
				setOriginalPatient(localPatient);
				handleMenuClose();
			} else {
				console.error("Failed to save patient:", res);
				// You may want to show a snackbar or alert here
			}
		} catch (err) {
			console.error("Error saving patient:", err);
		} finally {
			setSavingPatient(false);
			setTimeout(() => setSaveSuccess(false), 1500);
		}
	};

	const handleEventClick = async (event) => {
		const dateStr = event.date_of_service;

		if (!event.session_id) {
			console.warn("No session_id found in event");
			return;
		}

		try {
			const res = await globalAxios.get(
				`${SERVER_URL}/search/session/${event.session_id}`
			);
			const session = res.data;

			const sessionStartTime = session.start || "08:00";
			const sessionStart = `${session.date_of_service}T${sessionStartTime}:00`;
			const sessionEnd = calculateEndTime(
				session.date_of_service,
				sessionStartTime,
				null
			);

			dispatch(
				setSelectedSession({
					title: session.hospital?.name || "Session",
					id: session.id,
					start: sessionStart,
					end: sessionEnd,
					surgeon: {
						...session.surgeon,
						name: `${session.surgeon?.first_name || ""} ${
							session.surgeon?.last_name || ""
						}`,
					},
					hospital: session.hospital,
					events: session.events,
				})
			);

			const matchedEvent = session.events.find((e) => e.id === event.id);
			if (matchedEvent) {
				dispatch(setSelectedEvent(matchedEvent));
			} else {
				console.warn(
					"Could not find matching event in session response"
				);
			}

			dispatch(setCalendarDate(dateStr));
			dispatch(setCalendarView("dayGridMonth"));
			dispatch(setSelectedEntityType("event"));
			navigate("/calendar");
		} catch (err) {
			console.error("Error fetching session:", err);
		}
	};

	const handleBack = () => {
		dispatch(setSelectedItem(null));
	};

	const {
		first_name,
		last_name,
		title,
		date_of_birth,
		gender,
		address,
		suburb,
		postcode,
		email,
		home_number,
		mobile_number,
		work_number,
		next_of_kin,
		medicare,
		events = [],
	} = localPatient;

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<Box sx={{ maxWidth: "1200px", width: "100%", px: 2 }}>
				<Box
					sx={{
						display: "flex",
						mb: 4,
						flexWrap: "nowrap",
						alignItems: "stretch",
					}}
				>
					{/* Back Button */}
					<Button
						variant="outlined"
						onClick={handleBack}
						sx={{
							borderRadius: "8px 0 0 8px",
							borderRight: "none",
							backgroundColor: "grey.200",
							color: "text.primary",
							borderColor: "grey.400",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							minWidth: 56,
							px: 1,
							py: 1.5,
							"&:hover": {
								backgroundColor: "grey.300",
								borderColor: "grey.400",
							},
							borderTopRightRadius: 0,
							borderBottomRightRadius: 0,
						}}
					>
						<ArrowBackIcon />
					</Button>

					{/* Patient Details Box */}
					<Box
						sx={{
							border: "1px solid #ccc",
							borderLeft: "none",
							borderRadius: "0 8px 8px 0",
							flex: 1,
							backgroundColor: "#fafafa",
						}}
					>
						{/* Header */}
						<Box
							sx={{
								borderRadius: "0 7px 0 0",
								backgroundColor: (theme) =>
									theme.palette.primary.main,
								color: (theme) =>
									theme.palette.primary.contrastText,
								px: 2,
								pt: 0.2,
								pb: 0.5,
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
							}}
						>
							<Typography variant="h6" fontWeight="bold">
								Patient Details
							</Typography>

							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
								}}
							>
								{savingPatient ? (
									<SaveIcon
										color="warning"
										fontSize="small"
										sx={{
											animation:
												"pulseInward 1s infinite",
										}}
									/>
								) : saveSuccess ? (
									<SaveIcon
										color="success"
										fontSize="small"
										sx={{
											animation:
												"fadeOutSuccess 1.5s forwards",
										}}
									/>
								) : changesMade ? (
									<Tooltip title="Unsaved changes">
										<WarningAmberIcon
											color="warning"
											fontSize="small"
										/>
									</Tooltip>
								) : null}

								<IconButton
									size="small"
									sx={{ color: "inherit", padding: 0 }}
									onClick={handleMenuClick}
								>
									<SettingsIcon />
								</IconButton>
							</Box>

							<Menu
								anchorEl={anchorEl}
								open={open}
								onClose={handleMenuClose}
								transformOrigin={{
									horizontal: "right",
									vertical: "top",
								}}
								anchorOrigin={{
									horizontal: "right",
									vertical: "bottom",
								}}
							>
								{!isEditing
									? [
											<MenuItem
												key="edit"
												onClick={handleToggleEdit}
											>
												Edit
											</MenuItem>,
									  ]
									: [
											<MenuItem
												key="save"
												onClick={handleSave}
											>
												Save
											</MenuItem>,
											<MenuItem
												key="cancel"
												onClick={handleCancel}
											>
												Cancel
											</MenuItem>,
									  ]}
							</Menu>
						</Box>

						{/* Body */}
						<Box sx={{ px: 2, py: 1 }}>
							{isEditing ? (
								<>
									<TextField
										label="Title"
										size="small"
										value={title || ""}
										fullWidth
										sx={{ mb: 1 }}
										onChange={handleChange("title")}
									/>
									<TextField
										label="First Name"
										size="small"
										value={first_name || ""}
										fullWidth
										sx={{ mb: 1 }}
										onChange={handleChange("first_name")}
									/>
									<TextField
										label="Last Name"
										size="small"
										value={last_name || ""}
										fullWidth
										sx={{ mb: 1 }}
										onChange={handleChange("last_name")}
									/>
									<DatePicker
										label="Date of Birth"
										format="dd/MM/yyyy"
										value={
											date_of_birth
												? new Date(date_of_birth)
												: null
										}
										onChange={(newDate) => {
											if (!newDate) return;

											// Only keep YYYY-MM-DD
											const formattedDate = newDate
												.toISOString()
												.split("T")[0];

											setLocalPatient((prev) => ({
												...prev,
												date_of_birth: formattedDate,
											}));
										}}
										slotProps={{
											textField: {
												size: "small",
												fullWidth: true,
												sx: { mb: 1 },
											},
										}}
									/>
									<TextField
										label="Gender"
										size="small"
										value={gender || ""}
										fullWidth
										sx={{ mb: 1 }}
										onChange={handleChange("gender")}
									/>
									<Divider sx={{ my: 1 }} />
									<TextField
										label="Address"
										size="small"
										value={address || ""}
										fullWidth
										sx={{ mb: 1 }}
										onChange={handleChange("address")}
									/>
									<TextField
										label="Suburb"
										size="small"
										value={suburb || ""}
										fullWidth
										sx={{ mb: 1 }}
										onChange={handleChange("suburb")}
									/>
									<TextField
										label="Postcode"
										size="small"
										value={postcode || ""}
										fullWidth
										sx={{ mb: 1 }}
										onChange={(e) => {
											const value = e.target.value;

											// Allow only if value is empty or exactly 1–4 digits
											if (
												value === "" ||
												/^\d{1,4}$/.test(value)
											) {
												handleChange("postcode")(e); // Stored as string
											}
										}}
										inputProps={{
											maxLength: 4,
											inputMode: "numeric",
											pattern: "\\d{4}",
										}}
									/>

									<TextField
										label="Email"
										size="small"
										value={email || ""}
										fullWidth
										sx={{ mb: 1 }}
										error={emailError}
										helperText={
											emailError
												? "Invalid email address"
												: ""
										}
										onChange={(e) => {
											const value = e.target.value;
											handleChange("email")(e);

											// Simple email regex check
											const isValid =
												value === "" ||
												/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
													value
												);

											setEmailError(!isValid);
										}}
										onBlur={() => {
											// Also revalidate on blur
											setEmailError(
												email !== "" &&
													!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
														email
													)
											);
										}}
									/>

									<TextField
										label="Home Phone"
										size="small"
										value={home_number || ""}
										fullWidth
										sx={{ mb: 1 }}
										onChange={(e) => {
											const value = e.target.value;
											// Allow only empty or digits
											if (
												value === "" ||
												/^\d+$/.test(value)
											) {
												handleChange("home_number")(e); // Stored as string
											}
										}}
										inputProps={{
											inputMode: "numeric",
											pattern: "\\d*",
										}}
									/>

									<TextField
										label="Mobile Phone"
										size="small"
										value={mobile_number || ""}
										fullWidth
										sx={{ mb: 1 }}
										onChange={(e) => {
											const value = e.target.value;
											if (
												value === "" ||
												/^\d+$/.test(value)
											) {
												handleChange("mobile_number")(
													e
												);
											}
										}}
										inputProps={{
											inputMode: "numeric",
											pattern: "\\d*",
										}}
									/>

									<TextField
										label="Work Phone"
										size="small"
										value={work_number || ""}
										fullWidth
										sx={{ mb: 1 }}
										onChange={(e) => {
											const value = e.target.value;
											if (
												value === "" ||
												/^\d+$/.test(value)
											) {
												handleChange("work_number")(e);
											}
										}}
										inputProps={{
											inputMode: "numeric",
											pattern: "\\d*",
										}}
									/>
									<Divider sx={{ my: 1 }} />
									<TextField
										label="Next of Kin"
										size="small"
										value={next_of_kin || ""}
										fullWidth
										sx={{ mb: 1 }}
										onChange={handleChange("next_of_kin")}
									/>
									<TextField
										label="Medicare"
										size="small"
										value={medicare || ""}
										fullWidth
										sx={{ mb: 1 }}
										error={
											medicare !== "" &&
											!/^\d{11}$/.test(medicare)
										}
										helperText={
											medicare !== "" &&
											!/^\d{11}$/.test(medicare)
												? "Medicare number must be exactly 11 digits"
												: ""
										}
										onChange={(e) => {
											const value = e.target.value;

											// Allow only digit input, but store as string
											if (
												value === "" ||
												/^\d{0,11}$/.test(value)
											) {
												handleChange("medicare")(e);
											}
										}}
										inputProps={{
											maxLength: 11,
											inputMode: "numeric",
											pattern: "\\d{11}",
										}}
									/>
								</>
							) : (
								<>
									<Typography variant="body2">
										<strong>Name:</strong> {title}{" "}
										{first_name} {last_name}
									</Typography>
									<Typography variant="body2">
										<strong>Date of Birth:</strong>{" "}
										{date_of_birth
											? new Date(
													date_of_birth
											  ).toLocaleDateString()
											: "—"}
									</Typography>
									<Typography variant="body2">
										<strong>Gender:</strong> {gender || "—"}
									</Typography>

									<Divider sx={{ my: 1 }} />

									<Typography variant="body2">
										<strong>Address:</strong>{" "}
										{address || "—"}
									</Typography>
									<Typography variant="body2">
										<strong>Suburb:</strong> {suburb || "—"}
									</Typography>
									<Typography variant="body2">
										<strong>Postcode:</strong>{" "}
										{postcode || "—"}
									</Typography>
									<Typography variant="body2">
										<strong>Email:</strong> {email || "—"}
									</Typography>
									<Typography variant="body2">
										<strong>Home:</strong>{" "}
										{home_number || "—"}
									</Typography>
									<Typography variant="body2">
										<strong>Mobile:</strong>{" "}
										{mobile_number || "—"}
									</Typography>
									<Typography variant="body2">
										<strong>Work:</strong>{" "}
										{work_number || "—"}
									</Typography>

									<Divider sx={{ my: 1 }} />

									<Typography variant="body2">
										<strong>Next of Kin:</strong>{" "}
										{next_of_kin || "—"}
									</Typography>
									<Typography variant="body2">
										<strong>Medicare:</strong>{" "}
										{medicare || "—"}
									</Typography>
								</>
							)}
						</Box>
					</Box>
				</Box>

				{/* Linked Events */}
				<Box
					sx={{
						display: "flex",
						flexWrap: "wrap",
						gap: 2,
					}}
				>
					{/* Linked Events */}
					<Box
						sx={{
							flex: 1,
							minWidth: { xs: "100%", md: "48%" },
							border: "1px solid #ccc",
							borderRadius: 2,
							backgroundColor: "#fafafa",
						}}
					>
						{/* Header */}
						<Box
							sx={{
								borderRadius: "7px 7px 0 0",
								backgroundColor: (theme) =>
									theme.palette.primary.main,
								color: (theme) =>
									theme.palette.primary.contrastText,
								px: 2,
								pt: 0.2,
								pb: 0.5,
							}}
						>
							<Typography variant="h6" fontWeight="bold">
								Linked Events
							</Typography>
						</Box>

						<Box sx={{ px: 2 }}>
							{loadingDetail ? (
								<>
									{Array.from({ length: 1 }).map((_, idx) => (
										<Box
											key={idx}
											sx={{
												display: "flex",
												alignItems: "center",
												mt: 1.5,
												mb: 1.5,
												gap: 2,
											}}
										>
											<Skeleton
												variant="circular"
												width={32}
												height={32}
											/>
											<Box sx={{ flex: 1 }}>
												<Skeleton
													height={20}
													width="80%"
												/>
												<Skeleton
													height={16}
													width="60%"
												/>
											</Box>
										</Box>
									))}
								</>
							) : events.length > 0 ? (
								<List dense>
									{events.map((event) => (
										<ListItem
											button
											key={event.id}
											onClick={() =>
												handleEventClick({
													...event,
													patient: selectedItem,
												})
											}
											sx={{
												cursor: "pointer",

												"&:hover": {
													backgroundColor: "#f5f5f5",
												},
											}}
										>
											<ListItemIcon>
												<MedicalServicesIcon />
											</ListItemIcon>
											<ListItemText
												primary={
													event.procedure
														? `Procedure: ${event.procedure}`
														: `Event ID: ${event.id}`
												}
												secondary={
													event.date_of_service
														? new Date(
																event.date_of_service
														  ).toLocaleDateString()
														: "No date"
												}
											/>
										</ListItem>
									))}
								</List>
							) : (
								<Typography variant="body2">
									No events found for this patient.
								</Typography>
							)}
						</Box>
					</Box>

					{/* Placeholder for new right-hand box */}
					<Box
						sx={{
							flex: 1,
							minWidth: { xs: "100%", md: "48%" },
							border: "1px dashed #ccc",
							borderRadius: 2,
							backgroundColor: "#f9f9f9",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							minHeight: "200px",
						}}
					>
						<Typography variant="body2" color="text.secondary">
							(New content coming soon...)
						</Typography>
					</Box>
				</Box>
			</Box>
		</LocalizationProvider>
	);
};

export default PatientView;
